import { Alert, type AlertColor, Slide, type SlideProps, Snackbar } from "@mui/material";

import { CoAppTextButton } from "../styled/global.styled";

// ─────────────────────────────────────────────────────────────────────────────
// UndoSnackbar — local-state snackbar with an inline UNDO action button.
//
// Prefer the global `<AlertSnackbar />` (Redux-driven) for one-off success /
// error / info toasts. Use this component when the toast needs an undo
// affordance whose handler is owned by the calling component (e.g. soft-delete
// undo, bulk-restore undo). The handler can't live in Redux state, so the
// snackbar takes its open/level/message + handler as props.
// ─────────────────────────────────────────────────────────────────────────────

const SlideRight = (props: SlideProps) => <Slide {...props} direction="right" />;

export interface UndoSnackbarProps {
	open: boolean;
	level: AlertColor | "";
	message: string;
	setOpen: (open: boolean) => void;
	setLevel: (level: AlertColor | "") => void;
	setMessage: (message: string) => void;
	undoHandler: () => void;
}

export default function UndoSnackbar(props: UndoSnackbarProps) {
	const { open, level, message, setOpen, setLevel, setMessage, undoHandler } = props;

	const reset = () => {
		setOpen(false);
		setLevel("");
		setMessage("");
	};

	const handleUndo = () => {
		undoHandler();
		reset();
	};

	return (
		<Snackbar
			autoHideDuration={6000}
			disableWindowBlurListener
			open={open}
			onClose={reset}
			TransitionComponent={SlideRight}
		>
			<Alert
				onClick={handleUndo}
				severity={level !== "" ? level : "info"}
				sx={{ alignItems: "center" }}
			>
				{message}
				<CoAppTextButton
					onClick={handleUndo}
					sx={{ ":hover": { backgroundColor: "transparent" }, padding: "0px" }}
				>
					UNDO
				</CoAppTextButton>
			</Alert>
		</Snackbar>
	);
}
